import { CircularProgress, FormControl, IconButton, MenuItem, Select, Snackbar, Tooltip, Typography, TextField } from '@material-ui/core';
import { Star, StarBorder } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import apiConfig, { baseUrl } from 'apiConfig';
import { PERMISSIONS } from 'common/permissions';
import jwt_decode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import axios from 'utils/axios';
import localStorage from 'utils/localStorage';

const useStyles = makeStyles(theme => ({
  select: {
    backgroundColor: '#ffffff',
    fontSize: theme.spacing(2),
    '& div': {
      paddingTop: 10,
      paddingBottom: 10,
      minWidth: 90
    }
  },
  autocomplete: {
    backgroundColor: '#ffffff',
    minWidth: 200,
    maxWidth: 300,
    [theme.breakpoints.down('md')]: {
      minWidth: 150,
      maxWidth: 200
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 120,
      maxWidth: 150
    },
    '& .MuiInputBase-root': {
      fontSize: theme.spacing(2),
      paddingTop: 10,
      paddingBottom: 10,
      minWidth: 90,
      width: '100%'
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: '10px !important',
      paddingBottom: '10px !important',
      width: '100% !important'
    },
    '& .MuiOutlinedInput-root': {
      width: '100%'
    }
  },
  menuProps: {
    marginTop: 34,
    marginLeft: 1,
    minWidth: '134px !important'
  },
  companyName: {
    fontSize: 17,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    textAlign: 'center'
  },
  selectCompany: {
    minWidth: 200,
    maxWidth: 300,
    [theme.breakpoints.down('md')]: {
      minWidth: 150,
      maxWidth: 200
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 120,
      maxWidth: 150
    },
    '& .MuiInputBase-root': {
      width: '100%',
      minWidth: 0
    }
  },
  '.MuiTooltip-tooltipPlacementBottom': {
    paddingTop: 0
  },
  defaultCompanyButton: {
    padding: 4,
    marginLeft: 2
  }
}));

const ChangeLocation = ({ color }) => {

  const classes = useStyles();
  const user = localStorage.getFullInfo();
  const cId = localStorage.getCompanyId();

  const [listCompany, setListCompany] = useState(localStorage.getListCompany());
  const [company, setCompany] = useState(cId ? cId : user?.cid);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isStatus, setIsStatus] = useState({ failed: false, msg: '' });
  const [openSnackbar, setOpenSnackBar] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  // Set once per page load by a successful "set as default" call - there's no isDefault flag
  // on the company list from the API (GetActiveCompaniesBasic doesn't return one), so this
  // only reflects what happened THIS session, not the user's actual current default. Good
  // enough for "confirm the click worked" without a backend response-shape change.
  const [justSetDefaultId, setJustSetDefaultId] = useState(null);

  // Determine if we should use searchable dropdown (5+ companies)
  const useSearchableDropdown = listCompany && listCompany.length >= 5;

  const updateCompany = id => {
    setLoading(true);
    axios
      .post(
        apiConfig.url.AUTH_URL +
          apiConfig.url.COMPANY_CHANGE.replace('{companyId}', id)
      )
      .then(response => {
        const token = {
          token: response.data.token,
          expiresOn: response.data.expiresOn
        };
        const usr = {
          displayName: response.data.displayName,
          avatar: response.data.avatar
        };
        const info = jwt_decode(token.token);
        localStorage.set('companyId', info.cid);
        localStorage.set('sw-role', JSON.stringify(info.role));
        localStorage.set('sw-token', JSON.stringify(token));
        localStorage.set('sw-usr', JSON.stringify(usr));
        setIsStatus({ failed: false, msg: 'Company has been changed.' });
        setOpenSnackBar(true);

        const defaultPage = info.role.includes(PERMISSIONS.FIELD_TECHNICIAN)
          ? 'employees/today-route'
          : 'dashboard';
        window.location.href = baseUrl() + defaultPage;
      })
      .catch(e => {
        setCompany(cId);
        setIsStatus({
          failed: true,
          msg: 'Change failed, please try again later'
        });
        setOpenSnackBar(true);
      })
      .finally(() => setLoading(false));
  };

  // WI 4429: self-service default-company selection. Deliberately separate from the
  // admin-facing "Default Company" checkbox on a user's Security tab (which is disabled while
  // viewing your own profile) - operates on whichever company is currently active (`company`),
  // not a specific row, so the user switches to a location first (existing control above) and
  // then marks that as their default.
  const setAsDefault = companyId => {
    setSettingDefault(true);
    axios
      .post(
        apiConfig.url.AUTH_URL +
          apiConfig.url.SET_DEFAULT_COMPANY.replace('{companyId}', companyId)
      )
      .then(() => {
        setJustSetDefaultId(companyId);
        setIsStatus({ failed: false, msg: 'Default company updated.' });
        setOpenSnackBar(true);
      })
      .catch(() => {
        setIsStatus({
          failed: true,
          msg: 'Could not update your default company, please try again later'
        });
        setOpenSnackBar(true);
      })
      .finally(() => setSettingDefault(false));
  };

  useEffect(() => {
    if (disabled) {
      setTimeout(() => {
        setTooltipIsOpen(false);
      }, 0);
    }
  }, [disabled]);

  useEffect(() => {
    if (!listCompany && user) {
      // Always ask the API for the caller's own company list rather than gating the call by
      // role client-side - GetActiveCompaniesBasic already scopes the response correctly per
      // role (System Admin: all, Group/Company Admin: managed companies, everyone else: their
      // own direct assignments), so a plain multi-company Office Staff/Company Admin/Field Tech
      // gets their real list here too instead of being silently limited to a single company (the
      // gap that made "set as default" unreachable for anyone who wasn't a Group/System Admin).
      axios
        .get(apiConfig.url.BASE_URL + apiConfig.url.COMPANY_ACTIVE_BASIC)
        .then(res => {
          setListCompany(res.data);
          localStorage.set('listCompany', JSON.stringify(res.data));
        })
        .catch(() => {
          // Fallback to single company from user token
          const fallbackCompany = [{
            id: user.cid,
            name: user.company || 'Company'
          }];
          setListCompany(fallbackCompany);
          localStorage.set('listCompany', JSON.stringify(fallbackCompany));
        });
    }
  }, []);

  // Set selected company when listCompany is loaded
  useEffect(() => {
    if (listCompany && listCompany.length > 0) {
      const currentCompany = listCompany.find(comp => comp.id === company);
      setSelectedCompany(currentCompany || listCompany[0]);
    }
  }, [listCompany, company]);

  return (
    <>
      {listCompany && listCompany.length === 1 && (
        <Typography variant="h4" className={classes.companyName} style={{ color }}>
          {listCompany && listCompany.length > 0 && listCompany[0].name}
        </Typography>
      )}
      {listCompany && listCompany.length > 1 && !useSearchableDropdown && (
        <FormControl
          style={{ flexDirection: 'initial' }}
          className={`${classes.formControl} ${classes.selectCompany}`}
        >
          <Tooltip
            disableHoverListener={disabled}
            disableTouchListener={true}
            disableFocusListener={true}
            open={tooltipIsOpen}
            onOpen={() => setTooltipIsOpen(true)}
            onClose={() => setTooltipIsOpen(false)}
            arrow
            title={
              <>
                <Typography color="inherit">Change Location</Typography>
              </>
            }
          >
            <Select
              onOpen={() => {
                setDisabled(true);
              }}
              onClose={() => {
                setDisabled(true);
                setTimeout(() => {
                  setDisabled(false);
                }, 0);
                setTooltipIsOpen(false);
              }}
              defaultValue={company}
              value={company}
              variant="outlined"
              id="grouped-select"
              className={classes.select}
              MenuProps={{ classes: { paper: classes.menuProps } }}
              onChange={_e => {
                setCompany(_e.target.value);
                updateCompany(_e.target.value);
              }}
            >
              {listCompany &&
                listCompany.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}{item.city ? ` | ${item.city}, ${item.state}` : ''}
                  </MenuItem>
                ))}
            </Select>
          </Tooltip>
          {loading && (
            <CircularProgress
              style={{
                color,
                display: 'inline-flex',
                marginLeft: 10,
                marginTop: 6
              }}
              className={classes.circularProgress}
              size={24}
            />
          )}
          {!loading && (
            <Tooltip title="Set as my default company" arrow>
              <span>
                <IconButton
                  aria-label="Set as my default company"
                  className={classes.defaultCompanyButton}
                  disabled={settingDefault}
                  onClick={() => setAsDefault(company)}
                >
                  {justSetDefaultId === company
                    ? <Star style={{ color: '#f5b301' }} fontSize="small" />
                    : <StarBorder style={{ color }} fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </FormControl>
      )}
      {listCompany && listCompany.length > 1 && useSearchableDropdown && (
        <FormControl
          style={{ flexDirection: 'initial' }}
          className={`${classes.formControl} ${classes.selectCompany}`}
        >
          <Tooltip
            disableHoverListener={disabled}
            disableTouchListener={true}
            disableFocusListener={true}
            open={tooltipIsOpen}
            onOpen={() => setTooltipIsOpen(true)}
            onClose={() => setTooltipIsOpen(false)}
            arrow
            title={
              <>
                <Typography color="inherit">Change Location</Typography>
              </>
            }
          >
            <Autocomplete
              options={listCompany}
              getOptionLabel={(option) => option.name + (option.city ? ` | ${option.city}, ${option.state}` : '')}
              value={selectedCompany}
              onChange={(_event, newValue) => {
                if (newValue) {
                  setSelectedCompany(newValue);
                  setCompany(newValue.id);
                  updateCompany(newValue.id);
                }
              }}
              onOpen={() => {
                setDisabled(true);
              }}
              onClose={() => {
                setDisabled(true);
                setTimeout(() => {
                  setDisabled(false);
                }, 0);
                setTooltipIsOpen(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search locations..."
                  className={classes.autocomplete}
                />
              )}
              renderOption={(option) => (
                <Typography>{option.name}{option.city ? ` | ${option.city}, ${option.state}` : ''}</Typography>
              )}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter(option =>
                  option.name.toLowerCase().includes(q) ||
                  (option.city && option.city.toLowerCase().includes(q)) ||
                  (option.state && option.state.toLowerCase().includes(q))
                );
              }}
              noOptionsText="No locations found"
              clearOnEscape
              disableClearable
              size="small"
            />
          </Tooltip>
          {loading && (
            <CircularProgress
              style={{
                color,
                display: 'inline-flex',
                marginLeft: 10,
                marginTop: 6
              }}
              className={classes.circularProgress}
              size={24}
            />
          )}
          {!loading && (
            <Tooltip title="Set as my default company" arrow>
              <span>
                <IconButton
                  aria-label="Set as my default company"
                  className={classes.defaultCompanyButton}
                  disabled={settingDefault}
                  onClick={() => setAsDefault(company)}
                >
                  {justSetDefaultId === company
                    ? <Star style={{ color: '#f5b301' }} fontSize="small" />
                    : <StarBorder style={{ color }} fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </FormControl>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackBar(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity={isStatus.failed ? 'error' : 'success'}
        >
          <Typography color="inherit" variant="h6">
            {isStatus.msg}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}

export default ChangeLocation;
